import React, { useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PlusIcon, SendIcon, CreditCardIcon, ShieldCheckIcon, LockIcon, UnlockIcon, SettingsIcon, ArrowDownLeftIcon, ArrowUpRightIcon, ArrowRightIcon, EyeIcon, CopyIcon, WifiIcon, GlobeIcon, ShieldAlertIcon, FileTextIcon, ChevronRightIcon, SmartphoneIcon, CheckIcon } from 'lucide-react';
import { formatNaira, formatDate } from '../utils/format';
import { TopBar } from '../components/TopBar';
interface WalletProps {
  navigate: (screen: string) => void;
}
export function Wallet({
  navigate
}: WalletProps) {
  const [isCardFrozen, setIsCardFrozen] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const [showCopied, setShowCopied] = useState(false);
  const [cardToggles, setCardToggles] = useState({
    online: true,
    contactless: false,
    international: false
  });
  const handleViewPin = () => {
    setShowPin(true);
    setTimeout(() => setShowPin(false), 3000);
  };
  const handleCopyNumber = () => {
    setShowCopied(true);
    setTimeout(() => setShowCopied(false), 2000);
  };
  const handleCardToggle = (key: keyof typeof cardToggles) => {
    setCardToggles(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };
  const walletBalance = 2450000;
  const transactions = [{
    id: 1,
    type: 'payment',
    title: 'Premium: Child Education',
    date: '2026-03-15',
    amount: -16666,
    icon: ShieldCheckIcon,
    color: 'text-[#1a1a2e] bg-gray-100'
  }, {
    id: 2,
    type: 'fund',
    title: 'Wallet Funding via Paystack',
    date: '2026-03-14',
    amount: 50000,
    icon: ArrowDownLeftIcon,
    color: 'text-emerald-600 bg-emerald-100'
  }, {
    id: 3,
    type: 'claim',
    title: 'Claim Payout: Device Ins.',
    date: '2026-03-10',
    amount: 120000,
    icon: ArrowDownLeftIcon,
    color: 'text-emerald-600 bg-emerald-100'
  }, {
    id: 4,
    type: 'transfer',
    title: 'Transfer to GTBank',
    date: '2026-03-05',
    amount: -25000,
    icon: ArrowUpRightIcon,
    color: 'text-[#1a1a2e] bg-gray-100'
  }];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 10
    },
    show: {
      opacity: 1,
      y: 0
    }
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="flex-1 overflow-y-auto pb-24 bg-slate-50" data-id="element-938">
      <TopBar title="Wallet" onNotificationClick={() => navigate('notifications')} data-id="element-939" />

      <div className="px-5 pt-4 pb-6" data-id="element-940">
        {/* Balance Section */}
        <div className="text-center mb-10" data-id="element-941">
          <p className="text-gray-500 text-xs font-bold uppercase tracking-widest mb-2" data-id="element-942">
            Available Balance
          </p>
          <h1 className="text-5xl font-bold text-[#1B3B36] mb-3 tracking-tight drop-shadow-sm" data-id="element-943">
            {formatNaira(walletBalance)}
          </h1>
          <div className="inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-100 text-emerald-700 px-3 py-1.5 rounded-full text-xs font-bold shadow-sm" data-id="element-944">
            <ShieldCheckIcon className="w-3.5 h-3.5" data-id="element-945" /> Tier 2 Verified
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-center gap-6 mb-12" data-id="element-946">
          <button onClick={() => navigate('fund-wallet')} className="flex flex-col items-center gap-2 group" data-id="element-947">
            <div className="w-16 h-16 rounded-full bg-amber-500 text-[#1B3B36] flex items-center justify-center shadow-lg shadow-amber-500/30 group-active:scale-95 transition-transform" data-id="element-948">
              <PlusIcon className="w-7 h-7" strokeWidth={2.5} data-id="element-949" />
            </div>
            <span className="text-xs font-bold text-[#1B3B36]" data-id="element-950">Fund</span>
          </button>
          <button onClick={() => navigate('transfer')} className="flex flex-col items-center gap-2 group" data-id="element-951">
            <div className="w-16 h-16 rounded-full bg-white text-[#1B3B36] border border-gray-200 flex items-center justify-center shadow-sm group-active:scale-95 transition-transform hover:bg-gray-50" data-id="element-952">
              <SendIcon className="w-6 h-6" data-id="element-953" />
            </div>
            <span className="text-xs font-bold text-[#1B3B36]" data-id="element-954">Transfer</span>
          </button>
          <button className="flex flex-col items-center gap-2 group" data-id="element-955">
            <div className="w-16 h-16 rounded-full bg-white text-[#1B3B36] border border-gray-200 flex items-center justify-center shadow-sm group-active:scale-95 transition-transform hover:bg-gray-50" data-id="element-956">
              <CreditCardIcon className="w-6 h-6" data-id="element-957" />
            </div>
            <span className="text-xs font-bold text-[#1B3B36]" data-id="element-958">Pay Bills</span>
          </button>
        </div>

        {/* Virtual Card */}
        <div className="mb-8" data-id="element-959">
          <div className="flex justify-between items-end mb-4 px-1" data-id="element-960">
            <h3 className="text-lg font-bold text-[#1B3B36]" data-id="element-961">Skydd Card</h3>
          </div>

          <div className="relative perspective-1000" data-id="element-962">
            <motion.div animate={{
            rotateX: isCardFrozen ? 5 : 0,
            scale: isCardFrozen ? 0.98 : 1
          }} transition={{
            type: 'spring',
            damping: 20
          }} className={`relative rounded-3xl p-6 h-52 text-white shadow-2xl transition-all duration-500 overflow-hidden ${isCardFrozen ? 'bg-gray-400 shadow-none' : 'bg-gradient-to-br from-[#0F2B27] via-[#1B3B36] to-[#2A5A4A] shadow-emerald-900/30'}`} data-id="element-963">
              {/* Decorative elements */}
              {!isCardFrozen && <>
                  <div className="absolute top-4 right-4 w-32 h-32 rounded-full border-[12px] border-amber-500/10" data-id="element-964"></div>
                  <div className="absolute top-12 right-16 w-24 h-24 rounded-full border-[8px] border-emerald-500/10" data-id="element-965"></div>
                </>}

              {isCardFrozen && <div className="absolute inset-0 bg-white/30 backdrop-blur-md flex items-center justify-center z-20" data-id="element-966">
                  <div className="bg-[#1B3B36]/90 px-5 py-2.5 rounded-full flex items-center gap-2 shadow-xl" data-id="element-967">
                    <LockIcon className="w-4 h-4 text-amber-400" data-id="element-968" />
                    <span className="font-bold text-sm tracking-wide" data-id="element-969">
                      Card Frozen
                    </span>
                  </div>
                </div>}

              <AnimatePresence data-id="element-970">
                {showPin && !isCardFrozen && <motion.div initial={{
                opacity: 0,
                scale: 0.9
              }} animate={{
                opacity: 1,
                scale: 1
              }} exit={{
                opacity: 0,
                scale: 0.9
              }} className="absolute inset-0 bg-[#1B3B36]/60 backdrop-blur-md flex items-center justify-center z-30 rounded-3xl" data-id="element-971">
                    <div className="bg-white/10 border border-white/20 px-6 py-3 rounded-2xl flex flex-col items-center gap-1 shadow-2xl" data-id="element-972">
                      <span className="text-xs font-bold text-white/70 uppercase tracking-widest" data-id="element-973">
                        Your PIN
                      </span>
                      <span className="text-2xl font-mono font-bold tracking-[0.3em] text-white" data-id="element-974">
                        ••1234
                      </span>
                    </div>
                  </motion.div>}
              </AnimatePresence>

              <div className={`relative z-10 h-full flex flex-col justify-between ${isCardFrozen ? 'opacity-40 grayscale' : ''}`} data-id="element-975">
                <div className="flex justify-between items-start" data-id="element-976">
                  <div className="font-bold text-xl tracking-[0.2em] text-white/90" data-id="element-977">
                    SKYDD
                  </div>
                  <div className="w-12 h-9 bg-gradient-to-br from-amber-200 to-amber-400 rounded-md shadow-sm opacity-90" data-id="element-978"></div>
                </div>

                <div data-id="element-979">
                  <div className="text-2xl tracking-[0.15em] font-mono mb-3 text-white/90 drop-shadow-sm" data-id="element-980">
                    **** **** **** 4092
                  </div>
                  <div className="flex justify-between items-center text-sm text-white/80 font-medium" data-id="element-981">
                    <span className="uppercase tracking-widest text-xs" data-id="element-982">
                      Adaeze Okonkwo
                    </span>
                    <span className="font-mono tracking-wider" data-id="element-983">12/28</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Comprehensive Card Controls */}
          <div className="mt-6" data-id="element-984">
            {/* Horizontal Quick Actions */}
            <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide px-1 -mx-1" data-id="element-985">
              <button onClick={() => setIsCardFrozen(!isCardFrozen)} className="flex flex-col items-center gap-2 min-w-[72px] group" data-id="element-986">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center shadow-sm transition-colors ${isCardFrozen ? 'bg-[#1B3B36] text-white' : 'bg-white border border-gray-200 text-[#1B3B36] group-hover:bg-gray-50'}`} data-id="element-987">
                  {isCardFrozen ? <UnlockIcon className="w-5 h-5" data-id="element-988" /> : <LockIcon className="w-5 h-5" data-id="element-989" />}
                </div>
                <span className="text-[11px] font-bold text-[#1B3B36] text-center leading-tight" data-id="element-990">
                  {isCardFrozen ? 'Unfreeze' : 'Freeze'}
                </span>
              </button>

              <button onClick={handleViewPin} disabled={isCardFrozen} className="flex flex-col items-center gap-2 min-w-[72px] group disabled:opacity-50" data-id="element-991">
                <div className="w-12 h-12 rounded-full bg-white border border-gray-200 text-[#1B3B36] flex items-center justify-center shadow-sm group-hover:bg-gray-50 transition-colors" data-id="element-992">
                  <EyeIcon className="w-5 h-5" data-id="element-993" />
                </div>
                <span className="text-[11px] font-bold text-[#1B3B36] text-center leading-tight" data-id="element-994">
                  View PIN
                </span>
              </button>

              <button onClick={handleCopyNumber} className="flex flex-col items-center gap-2 min-w-[72px] group" data-id="element-995">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center shadow-sm transition-colors ${showCopied ? 'bg-emerald-50 border border-emerald-200 text-emerald-600' : 'bg-white border border-gray-200 text-[#1B3B36] group-hover:bg-gray-50'}`} data-id="element-996">
                  {showCopied ? <CheckIcon className="w-5 h-5" data-id="element-997" /> : <CopyIcon className="w-5 h-5" data-id="element-998" />}
                </div>
                <span className={`text-[11px] font-bold text-center leading-tight ${showCopied ? 'text-emerald-600' : 'text-[#1B3B36]'}`} data-id="element-999">
                  {showCopied ? 'Copied!' : 'Copy No.'}
                </span>
              </button>

              <button onClick={() => navigate('card-settings')} className="flex flex-col items-center gap-2 min-w-[72px] group" data-id="element-1000">
                <div className="w-12 h-12 rounded-full bg-white border border-gray-200 text-[#1B3B36] flex items-center justify-center shadow-sm group-hover:bg-gray-50 transition-colors" data-id="element-1001">
                  <SettingsIcon className="w-5 h-5" data-id="element-1002" />
                </div>
                <span className="text-[11px] font-bold text-[#1B3B36] text-center leading-tight" data-id="element-1003">
                  Limits
                </span>
              </button>

              <button className="flex flex-col items-center gap-2 min-w-[72px] group pr-4" data-id="element-1004">
                <div className="w-12 h-12 rounded-full bg-rose-50 border border-rose-100 text-rose-500 flex items-center justify-center shadow-sm group-hover:bg-rose-100 transition-colors" data-id="element-1005">
                  <ShieldAlertIcon className="w-5 h-5" data-id="element-1006" />
                </div>
                <span className="text-[11px] font-bold text-rose-600 text-center leading-tight" data-id="element-1007">
                  Block
                </span>
              </button>

              <button className="flex flex-col items-center gap-2 min-w-[72px] group pr-4" data-id="element-1008">
                <div className="w-12 h-12 rounded-full bg-white border border-gray-200 text-[#1B3B36] flex items-center justify-center shadow-sm group-hover:bg-gray-50 transition-colors" data-id="element-1009">
                  <FileTextIcon className="w-5 h-5" data-id="element-1010" />
                </div>
                <span className="text-[11px] font-bold text-[#1B3B36] text-center leading-tight" data-id="element-1011">
                  Statement
                </span>
              </button>
            </div>

            {/* Quick Toggles */}
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 mt-2" data-id="element-1012">
              <div className="space-y-1" data-id="element-1013">
                <div className="flex items-center justify-between py-2 border-b border-gray-50" data-id="element-1014">
                  <div className="flex items-center gap-3" data-id="element-1015">
                    <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center text-gray-500" data-id="element-1016">
                      <SmartphoneIcon className="w-4 h-4" data-id="element-1017" />
                    </div>
                    <div data-id="element-1018">
                      <p className="text-sm font-bold text-[#1B3B36]" data-id="element-1019">
                        Online Transactions
                      </p>
                      <p className="text-[10px] font-medium text-gray-500" data-id="element-1020">
                        Websites and apps
                      </p>
                    </div>
                  </div>
                  <button onClick={() => handleCardToggle('online')} data-id="element-1021">
                    <div className={`w-10 h-5.5 rounded-full relative shadow-inner transition-colors flex items-center px-0.5 ${cardToggles.online ? 'bg-emerald-500' : 'bg-gray-300'}`} data-id="element-1022">
                      <div className={`w-4.5 h-4.5 bg-white rounded-full shadow-sm transition-all ${cardToggles.online ? 'ml-auto' : 'mr-auto'}`} data-id="element-1023"></div>
                    </div>
                  </button>
                </div>

                <div className="flex items-center justify-between py-2 border-b border-gray-50" data-id="element-1024">
                  <div className="flex items-center gap-3" data-id="element-1025">
                    <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center text-gray-500" data-id="element-1026">
                      <WifiIcon className="w-4 h-4" data-id="element-1027" />
                    </div>
                    <div data-id="element-1028">
                      <p className="text-sm font-bold text-[#1B3B36]" data-id="element-1029">
                        Contactless Payments
                      </p>
                      <p className="text-[10px] font-medium text-gray-500" data-id="element-1030">
                        Tap to pay
                      </p>
                    </div>
                  </div>
                  <button onClick={() => handleCardToggle('contactless')} data-id="element-1031">
                    <div className={`w-10 h-5.5 rounded-full relative shadow-inner transition-colors flex items-center px-0.5 ${cardToggles.contactless ? 'bg-emerald-500' : 'bg-gray-300'}`} data-id="element-1032">
                      <div className={`w-4.5 h-4.5 bg-white rounded-full shadow-sm transition-all ${cardToggles.contactless ? 'ml-auto' : 'mr-auto'}`} data-id="element-1033"></div>
                    </div>
                  </button>
                </div>

                <div className="flex items-center justify-between py-2" data-id="element-1034">
                  <div className="flex items-center gap-3" data-id="element-1035">
                    <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center text-gray-500" data-id="element-1036">
                      <GlobeIcon className="w-4 h-4" data-id="element-1037" />
                    </div>
                    <div data-id="element-1038">
                      <p className="text-sm font-bold text-[#1B3B36]" data-id="element-1039">
                        International
                      </p>
                      <p className="text-[10px] font-medium text-gray-500" data-id="element-1040">
                        Outside Nigeria
                      </p>
                    </div>
                  </div>
                  <button onClick={() => handleCardToggle('international')} data-id="element-1041">
                    <div className={`w-10 h-5.5 rounded-full relative shadow-inner transition-colors flex items-center px-0.5 ${cardToggles.international ? 'bg-emerald-500' : 'bg-gray-300'}`} data-id="element-1042">
                      <div className={`w-4.5 h-4.5 bg-white rounded-full shadow-sm transition-all ${cardToggles.international ? 'ml-auto' : 'mr-auto'}`} data-id="element-1043"></div>
                    </div>
                  </button>
                </div>
              </div>
            </div>

            {/* Manage All Link */}
            <button onClick={() => navigate('card-settings')} className="w-full mt-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-xl flex items-center justify-center gap-1 text-xs font-bold text-[#1B3B36] transition-colors" data-id="element-1044">
              Manage All Card Settings{' '}
              <ChevronRightIcon className="w-3.5 h-3.5" data-id="element-1045" />
            </button>
          </div>
        </div>

        {/* Request Physical Card Banner */}
        <div className="bg-gradient-to-r from-gray-900 to-[#1B3B36] rounded-2xl p-4 mb-10 flex items-center justify-between text-white shadow-lg cursor-pointer hover:shadow-xl transition-all active:scale-[0.98]" data-id="element-1046">
          <div className="flex items-center gap-3" data-id="element-1047">
            <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center backdrop-blur-sm" data-id="element-1048">
              <CreditCardIcon className="w-5 h-5 text-amber-400" data-id="element-1049" />
            </div>
            <div data-id="element-1050">
              <h4 className="font-bold text-sm" data-id="element-1051">Request Physical Card</h4>
              <p className="text-[10px] text-gray-400 mt-0.5" data-id="element-1052">
                Delivered to your door in 3 days
              </p>
            </div>
          </div>
          <ArrowRightIcon className="w-5 h-5 text-gray-400" data-id="element-1053" />
        </div>

        {/* Transactions */}
        <div data-id="element-1054">
          <h3 className="text-lg font-bold text-[#1B3B36] mb-4 px-1" data-id="element-1055">
            Recent Transactions
          </h3>
          <motion.div variants={containerVariants} initial="hidden" animate="show" className="space-y-3" data-id="element-1056">
            {transactions.map(tx => <motion.div onClick={() => navigate('transaction-detail')} variants={itemVariants} key={tx.id} className="flex items-center justify-between bg-white p-4 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow cursor-pointer" data-id="element-1057">
                <div className="flex items-center gap-3" data-id="element-1058">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${tx.color}`} data-id="element-1059">
                    <tx.icon className="w-5 h-5" data-id="element-1060" />
                  </div>
                  <div data-id="element-1061">
                    <h4 className="font-bold text-[#1B3B36] text-sm" data-id="element-1062">
                      {tx.title}
                    </h4>
                    <p className="text-[11px] font-medium text-gray-500 mt-0.5" data-id="element-1063">
                      {formatDate(tx.date)}
                    </p>
                  </div>
                </div>
                <div className={`font-bold text-sm ${tx.amount > 0 ? 'text-emerald-600' : 'text-[#1B3B36]'}`} data-id="element-1064">
                  {tx.amount > 0 ? '+' : ''}
                  {formatNaira(tx.amount)}
                </div>
              </motion.div>)}
          </motion.div>
        </div>
      </div>
    </motion.div>;
}